"use client";

import { Activity, ExternalLink } from "lucide-react";

import { getLangSmithFieldsFromCache } from "@/lib/chat-stream-handlers";

// GCP project for Cloud Trace links - defaults to production
const GCP_PROJECT = process.env.NEXT_PUBLIC_GCP_PROJECT || "octogen-prod";

/**
 * Generates a Google Cloud Trace Explorer URL filtered by span attributes.
 * Uses the Trace Explorer view which provides a better waterfall visualization.
 * Prefers run_id for specific message traces, falls back to thread_id for conversation-level traces.
 */
function getCloudTraceUrl(runId?: string, threadId?: string): string {
  // Prefer run_id for specific message traces, fall back to thread_id
  const filterAttr = runId ? `conversation.run_id:${runId}` : `conversation.thread_id:${threadId}`;

  // Use Trace Explorer with span filter for better visualization
  // Format: /traces/explorer?project=X&spanFilter=hasLabel:attribute:value
  const spanFilter = encodeURIComponent(`hasLabel:${filterAttr}`);
  return `https://console.cloud.google.com/traces/explorer?project=${GCP_PROJECT}&spanFilter=${spanFilter}`;
}

/**
 * Generates a LangSmith trace URL for viewing agent traces.
 * URL format: https://smith.langchain.com/o/{workspace_id}/projects/p/{project_id}/r/{run_id}?trace_id={run_id}
 */
function getLangSmithTraceUrl(
  runId: string,
  langsmithWorkspaceId: string,
  langsmithProjectId: string
): string {
  return `https://smith.langchain.com/o/${langsmithWorkspaceId}/projects/p/${langsmithProjectId}/r/${runId}?trace_id=${runId}`;
}

interface TraceLinkProps {
  /** The OpenTelemetry run ID for the specific message trace (optional) */
  runId?: string;
  /** The thread/conversation ID for conversation-level traces */
  threadId?: string;
  /** LangSmith project name for display purposes */
  langsmithProject?: string;
  /** LangSmith workspace ID for constructing LangSmith trace URLs */
  langsmithWorkspaceId?: string;
  /** LangSmith project ID (UUID) for constructing LangSmith trace URLs */
  langsmithProjectId?: string;
  /** Optional className for styling */
  className?: string;
}

/**
 * A prominent debug badge that links to Google Cloud Trace and LangSmith.
 * Shows trace filtered by run_id if available, otherwise by thread_id.
 * Styled consistently with other debug mode elements.
 */
export function TraceLink({
  runId,
  threadId,
  langsmithProject,
  langsmithWorkspaceId,
  langsmithProjectId,
  className = "",
}: TraceLinkProps) {
  // Short-circuit: skip cache lookup if runId and all LangSmith fields are already provided
  const needsCacheLookup = !runId || !langsmithProjectId || !langsmithWorkspaceId;
  const cachedFields = needsCacheLookup ? getLangSmithFieldsFromCache(runId, threadId) : undefined;

  // Use cached runId if message's runId is not available (e.g., loaded from history)
  const effectiveRunId = runId || cachedFields?.runId;
  const effectiveProject = langsmithProject || cachedFields?.langsmithProject;
  const effectiveWorkspaceId = langsmithWorkspaceId || cachedFields?.langsmithWorkspaceId;
  const effectiveProjectId = langsmithProjectId || cachedFields?.langsmithProjectId;

  // Need at least one identifier to show the GCP link
  const hasGcpTrace = effectiveRunId || threadId;
  // Need run_id, workspace_id, and project_id for LangSmith link
  const hasLangSmithTrace = effectiveRunId && effectiveWorkspaceId && effectiveProjectId;

  if (!hasGcpTrace && !hasLangSmithTrace) {
    return null;
  }

  const gcpTraceUrl = hasGcpTrace ? getCloudTraceUrl(effectiveRunId, threadId) : null;
  // Safe to cast: hasLangSmithTrace is true only when all three are defined
  const langSmithUrl =
    hasLangSmithTrace && effectiveRunId && effectiveWorkspaceId && effectiveProjectId
      ? getLangSmithTraceUrl(effectiveRunId, effectiveWorkspaceId, effectiveProjectId)
      : null;
  const identifier = effectiveRunId ? `run_id: ${effectiveRunId}` : `thread_id: ${threadId}`;

  const linkBaseClass =
    "inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[10px] shadow transition-colors";

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      {gcpTraceUrl && (
        <a
          href={gcpTraceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${linkBaseClass} bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white`}
          title={`View trace in Cloud Console (${identifier})`}
          onClick={(e) => e.stopPropagation()}
        >
          <Activity className="h-3 w-3" />
          <span>GCP</span>
        </a>
      )}
      {langSmithUrl && (
        <a
          href={langSmithUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${linkBaseClass} bg-purple-800 text-purple-200 hover:bg-purple-700 hover:text-white`}
          title={`View trace in LangSmith (${effectiveProject || "project"})`}
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="h-3 w-3" />
          <span>LS</span>
        </a>
      )}
    </span>
  );
}
